import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sun, AlertTriangle } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = React.useRef(false);
  const [err, setErr] = React.useState(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    const run = async () => {
      if (!sessionId) {
        navigate("/login");
        return;
      }
      try {
        const api = (await import("@/lib/api")).default;
        const res = await api.post("/auth/session", { session_id: sessionId });
        setUser(res.data.user);
        const saved = sessionStorage.getItem("gf_next") || "";
        sessionStorage.removeItem("gf_next");
        const dest = saved.startsWith("/") && !saved.startsWith("//") ? saved : "/dashboard";
        window.history.replaceState(null, "", dest);
        navigate(dest, { state: { user: res.data.user } });
      } catch (e) {
        // Only bounce to /login for a genuine auth rejection. Transient edge/origin
        // blips (520/502/503/504 or network) are retried by the api client; if one still
        // slips through, keep the user here with a retry rather than logging them out.
        const status = e.response?.status;
        if (status === 401 || status === 403) {
          navigate("/login");
        } else {
          setErr(true);
        }
      }
    };
    run();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-callback">
      <div className="flex flex-col items-center gap-3 text-slate-500">
        {err ? (
          <>
            <AlertTriangle className="w-8 h-8 text-amber-500" />
            <div className="text-sm">We couldn't reach the server. Please try again.</div>
            <button
              onClick={() => window.location.reload()}
              className="mt-1 px-4 py-1.5 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800"
              data-testid="auth-callback-retry"
            >
              Retry
            </button>
          </>
        ) : (
          <>
            <Sun className="w-8 h-8 animate-spin text-blue-600" />
            <div className="text-sm">Signing you in…</div>
          </>
        )}
      </div>
    </div>
  );
}
